import { useEffect } from 'react'
import { useMedChronEditingLease } from '../../hooks/useMedChronEditingLease'
import { useAppDispatch } from '@/store/store-hooks'
import { isoDateStringToMonthDayYear } from '@/util/time'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackMedChronEvent } from '@/analytics/utils/medchron-analytics'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import MatterRecordRemoveFileDialog from '@/matter-asset-common/files/components/MatterRecordRemoveFileDialog'
import { MatterActivityFeedActions } from '@/matter/activity-feed/store/slice'
import { getRemovalImpact } from '../../fetch/get-removal-impact'
import { deleteAssetFile } from '../../fetch/delete-asset-file'
import { MedChronAssetActions } from '../../store/slice'
import { RemovalImpactResponse } from '../../store/schemas'
import { refreshMatterAfterWriteAccessError } from '../../utils/refresh-matter-after-write-access-error'

type RemoveFileDialogProps = {
  open: boolean
  assetId: string
  matterId: string
  userFileId: string
  displayName: string
  onClose: () => void
}

/**
 * Remove-file confirmation for the chronology. Thin wrapper over the shared RemoveFileDialog
 * that supplies the med-chron copy, impact fetch and sentence, and the removal itself.
 */
export default function RemoveFileDialog({ open, assetId, matterId, userFileId, displayName, onClose }: RemoveFileDialogProps) {
  const dispatch = useAppDispatch()
  const lease = useMedChronEditingLease()
  useEffect(() => {
    if (!lease.canWrite) onClose()
  }, [lease.canWrite, onClose])

  const describeImpact = (impact: RemovalImpactResponse) => {
    const eventsPhrase = `${impact.events_count} event${impact.events_count === 1 ? '' : 's'}`
    if (impact.earliest_date_of_service && impact.latest_date_of_service) {
      return `Removing this file will delete ${eventsPhrase} from ${isoDateStringToMonthDayYear(
        impact.earliest_date_of_service
      )} – ${isoDateStringToMonthDayYear(impact.latest_date_of_service)} and regenerate the chronology.`
    }
    return `Removing this file will delete ${eventsPhrase} and regenerate the chronology.`
  }

  const handleConfirm = async () => {
    if (!lease.canWrite) {
      onClose()
      return
    }
    let result
    try {
      result = await deleteAssetFile(assetId, userFileId)
    } catch (error) {
      if (lease.handleError(error)) {
        onClose()
        return
      }
      refreshMatterAfterWriteAccessError(error, matterId, dispatch)
      throw error
    }
    trackMedChronEvent(AnalyticsEvent.MedChronAssetFileRemoved, { medChronAssetId: assetId, medChronEventsRemoved: result.events_removed })
    // Server truth: refresh the overview, events, and roster after the regeneration
    dispatch(MedChronAssetActions.fetchAssetForMatter({ matterId }))
    dispatch(MedChronAssetActions.refreshAssetData({ assetId }))
    // The removal is a completed change to the record, which moves its row in the matter's activity.
    dispatch(MatterActivityFeedActions.invalidateMatterActivity({ matterId }))
    // Removal is immediate (no regeneration wait), so confirm it with a toast.
    dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Removed ${displayName} from the chronology`, durationMs: kToastDurationShortMs }))
  }

  return (
    <MatterRecordRemoveFileDialog
      open={open}
      displayName={displayName}
      onClose={onClose}
      title="Remove file from chronology"
      confirmLabel="Remove and regenerate"
      impactKey={`${assetId}:${userFileId}`}
      fetchImpact={() =>
        getRemovalImpact(assetId, userFileId).catch((error) => {
          refreshMatterAfterWriteAccessError(error, matterId, dispatch)
          throw error
        })
      }
      describeImpact={describeImpact}
      impactUnavailableMessage="The impact summary could not be loaded. Removing this file will delete its events and regenerate the chronology."
      conflictMessage="The chronology is being updated. Try again when the current update finishes."
      onConfirm={handleConfirm}
    />
  )
}
